import React, { forwardRef, useEffect, useState } from "react";

import styled from "styled-components/macro";

import { scrollbarDarkThin, scrollbarLightThin } from "UI/scrollbarStyles";

import { defaultSize } from "./constants";
import { iconWidths } from "./interpolations";

const Textarea = forwardRef(
  (
    {
      value,
      setValue,
      id,
      icon,
      borderColor,
      backgroundColor = "#fff",
      textColor = "#333",
      minHeight = 80,
      maxHeight = 300,
      isDark,
      borderRadius,
      ["aria-label"]: ariaLabel,
      charLimit,
      setErrorText,
      autofocus,
      size = defaultSize,
      ...rest
    },
    textareaRef
  ) => {
    const [hasReachedMaxHeight, setHasReachedMaxHeight] = useState(false);

    useEffect(() => {
      if (textareaRef.current) {
        // We need to reset the height momentarily to get the correct scrollHeight for the textarea
        textareaRef.current.style.height = "0px";
        const scrollHeight = textareaRef.current.scrollHeight;

        // check scrollHeight value vs min & max height
        let scrollHeightWithinBounds = null;
        if (scrollHeight < minHeight) {
          scrollHeightWithinBounds = minHeight;
          setHasReachedMaxHeight(false);
        } else if (scrollHeight > maxHeight) {
          scrollHeightWithinBounds = maxHeight;
          setHasReachedMaxHeight(true);
        } else {
          scrollHeightWithinBounds = scrollHeight;
          setHasReachedMaxHeight(false);
        }

        // We then set the height directly, outside of the render loop
        // Trying to set this with state or a ref will product an incorrect value.
        textareaRef.current.style.height = `${scrollHeightWithinBounds}px`;
      }
    }, [textareaRef, value, maxHeight, minHeight]);

    const handleChange = (e) => {
      if (charLimit && charLimit < e.target.value.length) {
        setErrorText(`Only ${charLimit} characters are allowed`);
        return;
      }
      setErrorText("");
      setValue(e.target.value);
    };

    return (
      <>
        <TextareaStyled
          ref={textareaRef}
          value={value}
          id={id}
          onChange={handleChange}
          hasIcon={Boolean(icon)}
          $borderColor={borderColor}
          backgroundColor={backgroundColor}
          borderRadius={borderRadius}
          textColor={textColor}
          hasReachedMaxHeight={hasReachedMaxHeight}
          isDark={isDark}
          className="fnr-textarea"
          aria-label={ariaLabel}
          autoFocus={autofocus}
          $size={size}
          {...rest}
        />

        {/* This is so the textarea content doesn't overlap with the "halfway inside" label */}
        {hasReachedMaxHeight ? (
          <OverlapHidder backgroundColor={backgroundColor} />
        ) : null}
      </>
    );
  }
);

const TextareaStyled = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  font-size: 18px;
  border-radius: ${({ borderRadius }) => borderRadius || "10px"};
  border: 1px solid
    ${({ $borderColor = "fnGrey", theme: { colors } }) =>
      colors[$borderColor] || $borderColor};
  padding: 12px 16px;
  padding-left: ${({ hasIcon, $size }) =>
    hasIcon ? `${iconWidths[$size]}px` : null};
  color: ${({ textColor }) => textColor};
  outline: none;
  background: ${({ backgroundColor }) => backgroundColor};
  transition: border-color 0.2s, box-shadow 0.2s;
  font-family: inherit;
  resize: none;
  ${({ isDark }) => (isDark ? scrollbarDarkThin : scrollbarLightThin)};

  // sometimes the scrollbars are showing when unneeded...
  // so hiding them unless we have reached the maxHeight
  overflow-y: ${({ hasReachedMaxHeight }) =>
    hasReachedMaxHeight ? "auto" : "hidden"};
  ${({ $focusSize = "1px", $focusColor = "#278dc1" }) =>
    `&:focus {
    box-shadow: 0 0 0 ${$focusSize} ${$focusColor}b5;
  }`}
`;

const OverlapHidder = styled.div`
  position: absolute;
  background-color: ${({ backgroundColor }) => backgroundColor};
  height: 10px;
  top: 1px;
  left: 10px;
  right: 20px;
`;

export default Textarea;
